/* eslint-disable react/forbid-elements */
import {
  ArrowLeftIcon,
  Paragraph,
  QuotesIcon,
  Button,
  XStack,
  YStack,
  styled,
  ArrowRightIcon,
  Heading,
  useMedia,
} from '@bounty/creators-design-system'
import { AnimatePresence } from '@tamagui/animate-presence'
import { useState } from 'react'
import BeamLogo from '../../../assets/beam.png'
import OlipopLogo from '../../../assets/olipop.png'
import CloudsharksLogo from '../../../assets/cloudsharks.png'
import DreamlandLogo from '../../../assets/dreamland.png'
import BeamCoverImage from '../../../assets/beam-testimonial.jpg'
import OlipopCoverImage from '../../../assets/olipop-testimonial.jpg'
import CloudsharksCoverImage from '../../../assets/cloudsharks-testimonial.jpg'
import DreamlandCoverImage from '../../../assets/dreamland-testimonial.jpg'
import { Video } from '../../../components/Video'
import { VIDEO_BUCKET_URL } from '../../../config/env'

export const testimonials = [
  {
    logo: BeamLogo,
    coverImage: BeamCoverImage,
    videoId: '7176086218868821294',
    quote: 'The Bounty offer is the biggest converter for us to see videos.',
    company: 'Beam',
    person: 'Mike, President',
  },
  {
    logo: OlipopLogo,
    coverImage: OlipopCoverImage,
    videoId: '7117739907560377643',
    quote:
      'We can only work directly with so many creators at a time, but Bounty has allowed us to scale up our UGC efforts while helping build community along the way.',
    company: 'Olipop',
    person: 'Steven, Head of Partnerships',
  },
  {
    logo: CloudsharksLogo,
    coverImage: CloudsharksCoverImage,
    videoId: '7124180935792233774',
    quote:
      'Customers are paying for products and posting about us all over TikTok and the effects have been profound.',
    company: 'Cloudsharks',
    person: 'Rayan, Founder',
  },
  {
    logo: DreamlandLogo,
    coverImage: DreamlandCoverImage,
    videoId: '7176075008756976942',
    quote: 'Bounty has been transformative for our business.',
    company: 'Dreamland',
    person: 'Mike, Director of ecommerce',
  },
]

const YStackEnterable = styled(YStack, {
  variants: {
    isLeft: { true: { x: -300, opacity: 0 } },
    isRight: { true: { x: 300, opacity: 0 } },
  } as const,
}) as typeof YStack

const wrap = (min: number, max: number, v: number) => {
  const rangeSize = max - min
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min
}

export const Testimonials = () => {
  const { gtMd } = useMedia()
  const [[page, direction], setPage] = useState([0, 0])

  const testimonialIndex = wrap(0, testimonials.length, page)
  const testimonial = testimonials[testimonialIndex]

  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection])
  }

  const enterVariant = direction === 1 || direction === 0 ? 'isRight' : 'isLeft'
  const exitVariant = direction === 1 ? 'isLeft' : 'isRight'

  return (
    <XStack
      ov="hidden"
      pos="relative"
      h={800}
      backgroundColor={'rgba(92, 225, 230, 1)'}
      jc="center"
      w="100%"
      ai="center"
    >
      <AnimatePresence enterVariant={enterVariant} exitVariant={exitVariant}>
        <YStackEnterable
          key={page}
          animation="quick"
          fullscreen
          x={0}
          o={1}
          justifyContent="center"
        >
          <YStack
            px="$4"
            space="$6"
            alignItems="center"
            $gtMd={{ flexDirection: 'row', justifyContent: 'center' }}
            width="100%"
            maxWidth={900}
            alignSelf="center"
          >
            <YStack
              alignItems="center"
              flex={1}
              maxWidth={500}
              $gtMd={{ alignItems: 'flex-start', pr: '$8' }}
            >
              <YStack mb="$4" $gtMd={{ display: 'none' }}>
                <QuotesIcon size={24} color="rgba(56, 56, 57, 1)" />
              </YStack>
              <YStack mb="$4" display="none" $gtMd={{ display: 'flex' }}>
                <QuotesIcon size={32} color="rgba(56, 56, 57, 1)" />
              </YStack>
              <Heading
                mb="$4"
                textAlign="center"
                color="rgba(56, 56, 57, 1)"
                size="$xl"
                $gtMd={{ size: '$2xl', textAlign: 'left' }}
              >
                {testimonial.quote}
              </Heading>
              <YStack
                alignItems="center"
                space="$4"
                $gtMd={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <YStack>
                  <YStack mb="$3">
                    <img
                      alt={`${testimonial['company']} logo`}
                      loading="lazy"
                      src={testimonial['logo']}
                      style={{
                        height: gtMd ? '30px' : '22px',
                        objectFit: 'contain',
                        objectPosition: gtMd ? 'left' : 'center',
                      }}
                    />
                  </YStack>
                  <Paragraph
                    fontWeight={'$semibold'}
                    color="rgba(56, 56, 57, 1)"
                  >
                    {testimonial.person}
                  </Paragraph>
                </YStack>
                <XStack space="$4">
                  <Button
                    borderRadius={100000}
                    backgroundColor="rgba(56, 56, 57, 1)"
                    accessibilityLabel="Carousel left"
                    size="$lg"
                    event="Brands Testimonials Section Previous Button Clicked"
                    colorScheme="whiteAlpha"
                    onPress={() => paginate(-1)}
                  >
                    <ArrowLeftIcon size={18} />
                  </Button>
                  <Button
                    borderRadius={100000}
                    backgroundColor="rgba(56, 56, 57, 1)"
                    accessibilityLabel="Carousel right"
                    size="$lg"
                    event="Brands Testimonials Section Next Button Clicked"
                    colorScheme="whiteAlpha"
                    onPress={() => paginate(1)}
                  >
                    <ArrowRightIcon size={18} />
                  </Button>
                </XStack>
              </YStack>
            </YStack>
            <YStack
              borderRadius={'$5'}
              borderWidth={6}
              borderColor="$primary.200"
              overflow="hidden"
              width={220}
              height={220 * 1.72}
              $gtMd={{
                width: 260,
                height: 260 * 1.72,
              }}
            >
              <Video
                url={`${VIDEO_BUCKET_URL}/${testimonial['videoId']}.mp4`}
                poster={testimonial['coverImage']}
                playsInline
              />
            </YStack>
          </YStack>
        </YStackEnterable>
      </AnimatePresence>
    </XStack>
  )
}
